import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  getCountFromServer,
  doc,
} from "../lib/firestoreClient";
import { db } from "../lib/firebase";
import {
  LEADERBOARD_PAGE_SIZE,
  TIME_RANGES,
  IMPACT_TYPES,
} from "../constants/farmMarkConstants";
import { IMPACT_LEADERBOARD_CATEGORIES } from "./impactPointsService";
import { estimateCO2Saved } from "../utils/co2Calculator";
import { errorDev } from '../utils/logger.js';

const OVERALL_LEADERBOARD_KEY = "overall";

function cutoffDate(rangeDays) {
  if (!rangeDays) return null;
  const d = new Date();
  d.setDate(d.getDate() - rangeDays);
  return d;
}

function mapUserProfile(userDoc) {
  const data = userDoc?.data || {};
  return {
    userId: userDoc.userId,
    displayName: data.displayName || data.username || data.name || "User",
    username: data.username || "",
    photoURL: data.photoURL || data.avatar || "",
  };
}

async function attachProfiles(ranked) {
  if (!ranked?.length) return [];

  // Batch all profile reads in parallel. Each getDoc is a direct document
  // lookup by ID, which is reliable and does not depend on a composite
  // index the way a "where documentId in (...)" query would.
  const profilePairs = await Promise.all(
    ranked.map(async (entry) => {
      try {
        const snap = await getDoc(doc(db, "users", entry.userId));
        return {
          userId: entry.userId,
          data: snap.exists() ? snap.data() : {},
        };
      } catch (err) {
        errorDev("Operation failed:", err);
        return { userId: entry.userId, data: {} };
      }
    })
  );

  const profileMap = new Map(profilePairs.map((p) => [p.userId, p]));
  return ranked.map((entry) => {
    const profile = profileMap.get(entry.userId) || { userId: entry.userId, data: {} };
    return {
      ...mapUserProfile(profile),
      count: entry.count,
      totalIP: entry.totalIP,
    };
  });
}

/**
 * Count impact events per user, chunked under the Firestore `in` limit.
 * Used by the all-time leaderboards, which rank by the user's `points`
 * field and therefore have no per-event aggregation to fall back on.
 *
 * @param {string[]} userIds
 * @param {string|null} [category] optional event-category filter
 * @returns {Promise<Map<string, number>>}
 */
async function getEventCounts(userIds, category = null) {
  const uniqueIds = Array.from(new Set((userIds || []).filter(Boolean).map(String)));
  const counts = new Map();
  if (uniqueIds.length === 0) return counts;

  const chunkSize = 30;
  for (let i = 0; i < uniqueIds.length; i += chunkSize) {
    const chunk = uniqueIds.slice(i, i + chunkSize);
    try {
      const snap = await getDocs(
        query(collection(db, "impact_points_events"), where("userId", "in", chunk))
      );
      snap.docs.forEach((d) => {
        const ev = d.data() || {};
        if (category && ev.category !== category) return;
        const uid = String(ev.userId || "");
        counts.set(uid, (counts.get(uid) || 0) + 1);
      });
    } catch (err) {
      errorDev("Operation failed:", err);
    }
  }
  return counts;
}

async function getAllTimeOverallLeaderboard(max) {
  try {
    const usersSnap = await getDocs(
      query(collection(db, "users"), orderBy("points", "desc"), limit(Math.max(max * 3, 80)))
    );

    const ranked = usersSnap.docs
      .map((d) => {
        const data = d.data() || {};
        return {
          userId: d.id,
          data,
          count: 0,
          totalIP: safeNumber(data?.points),
        };
      })
      .filter((row) => row.totalIP > 0)
      .sort((a, b) => b.totalIP - a.totalIP)
      .slice(0, max);

    // The all-time query ranks by the user's points total, so it has no
    // event aggregation to source `count` from. Pull real per-user action
    // counts from the impact ledger instead of hardcoding 0.
    const counts = await getEventCounts(ranked.map((row) => row.userId));

    return ranked.map((row) => ({
      ...mapUserProfile({ userId: row.userId, data: row.data }),
      count: counts.get(row.userId) || 0,
      totalIP: row.totalIP,
    }));
  } catch (err) {
    errorDev("Operation failed:", err);
    return [];
  }
}

async function getTimeBoundOverallLeaderboard(rangeDays, max) {
  const cutoff = cutoffDate(rangeDays);
  if (!cutoff) return getAllTimeOverallLeaderboard(max);

  try {
    const eventsSnap = await getDocs(
      query(collection(db, "impact_points_events"), where("createdAt", ">=", cutoff))
    );

    const grouped = new Map();
    eventsSnap.docs.forEach((eventDoc) => {
      const event = eventDoc.data() || {};
      const userId = String(event.userId || "");
      if (!userId) return;
      const points = Number(event.points || 0);
      if (!grouped.has(userId)) {
        grouped.set(userId, { userId, count: 0, totalIP: 0 });
      }
      const entry = grouped.get(userId);
      entry.count += 1;
      entry.totalIP += Number.isFinite(points) ? points : 0;
    });

    const ranked = Array.from(grouped.values())
      .filter((entry) => entry.totalIP > 0)
      .sort((a, b) => b.totalIP - a.totalIP || b.count - a.count)
      .slice(0, max);

    if (ranked.length === 0) {
      return getAllTimeOverallLeaderboard(max);
    }

    return attachProfiles(ranked);
  } catch (err) {
    errorDev("Operation failed:", err);
    return getAllTimeOverallLeaderboard(max);
  }
}

function safeNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function mergeWithFallback(...values) {
  return values.reduce((max, current) => {
    const value = safeNumber(current);
    return value > max ? value : max;
  }, 0);
}

function mapImpactActionToBreakdown(action) {
  if (action === "product_posted") {
    return { field: "productListingPoints", type: IMPACT_TYPES.PRODUCT_LISTING };
  }
  if (action === "price_report_validated") {
    return { field: "priceValidatedPoints", type: IMPACT_TYPES.PRICE_CONTRIBUTION };
  }
  if (action === "product_rating_received") {
    return { field: "productRatingPoints", type: IMPACT_TYPES.RATING };
  }
  if (action === "helpful_feedback") {
    return { field: "helpfulFeedbackPoints", type: "fixers" };
  }
  return { field: "other", type: "other" };
}

function aggregateImpactEvents(events = []) {
  return events.reduce(
    (acc, event) => {
      const action = String(event?.action || "");
      const points = safeNumber(event?.points);
      const mapped = mapImpactActionToBreakdown(action);
      if (mapped.field in acc) {
        acc[mapped.field] += points;
      } else {
        acc.other += points;
      }
      return acc;
    },
    {
      productListingPoints: 0,
      productRatingPoints: 0,
      priceValidatedPoints: 0,
      helpfulFeedbackPoints: 0,
      other: 0,
    }
  );
}

function getProfileCategoryScore(data, category) {
  const categoryFromBreakdown = safeNumber(data?.impactBreakdown?.categories?.[category]);
  if (categoryFromBreakdown > 0) return categoryFromBreakdown;

  const productListingPoints = safeNumber(data?.impactBreakdown?.productListingPoints);
  const productRatingPoints = safeNumber(data?.impactBreakdown?.productRatingPoints);
  const priceValidatedPoints = safeNumber(data?.impactBreakdown?.priceValidatedPoints);
  const helpfulFeedbackPoints = safeNumber(data?.impactBreakdown?.helpfulFeedbackPoints);
  const ratingPoints = safeNumber(data?.ratingPoints);
  const productsListed = safeNumber(data?.productsListed);
  const pricesSubmitted = safeNumber(data?.pricesSubmitted);
  const totalPoints = safeNumber(data?.points);

  if (category === IMPACT_LEADERBOARD_CATEGORIES.TOP_GROWERS) {
    const score = productListingPoints + productRatingPoints + (productsListed * 5) + ratingPoints;
    return score > 0 ? score : totalPoints;
  }

  if (category === IMPACT_LEADERBOARD_CATEGORIES.PRICE_SCOUTS) {
    const score = priceValidatedPoints + (pricesSubmitted * 10);
    return score > 0 ? score : totalPoints;
  }

  if (category === IMPACT_LEADERBOARD_CATEGORIES.FIXERS) {
    return helpfulFeedbackPoints;
  }

  return totalPoints;
}

async function getAllTimeCategoryLeaderboard(category, max) {
  try {
    const usersSnap = await getDocs(
      query(collection(db, "users"), orderBy("points", "desc"), limit(Math.max(max * 6, 150)))
    );

    const ranked = usersSnap.docs
      .map((d) => {
        const data = d.data() || {};
        return {
          userId: d.id,
          data,
          count: 0,
          totalIP: getProfileCategoryScore(data, category),
        };
      })
      .filter((row) => row.totalIP > 0)
      .sort((a, b) => b.totalIP - a.totalIP)
      .slice(0, max);

    // Count actions in this category from the impact ledger rather than
    // the user's `points` total, which mixes every category together.
    const counts = await getEventCounts(ranked.map((row) => row.userId), category);

    return ranked.map((row) => ({
      ...mapUserProfile({ userId: row.userId, data: row.data }),
      count: counts.get(row.userId) || 0,
      totalIP: row.totalIP,
    }));
  } catch (err) {
    errorDev("Operation failed:", err);
    return [];
  }
}

async function getTimeBoundCategoryLeaderboard(category, rangeDays, max) {
  const cutoff = cutoffDate(rangeDays);
  if (!cutoff) return getAllTimeCategoryLeaderboard(category, max);

  try {
    const eventsSnap = await getDocs(
      query(collection(db, "impact_points_events"), where("createdAt", ">=", cutoff))
    );

    const grouped = new Map();
    eventsSnap.docs.forEach((eventDoc) => {
      const event = eventDoc.data() || {};
      if (event.category !== category) return;
      const userId = String(event.userId || "");
      if (!userId) return;
      const points = Number(event.points || 0);
      if (!grouped.has(userId)) {
        grouped.set(userId, { userId, count: 0, totalIP: 0 });
      }
      const entry = grouped.get(userId);
      entry.count += 1;
      entry.totalIP += Number.isFinite(points) ? points : 0;
    });

    const ranked = Array.from(grouped.values())
      .filter((entry) => entry.totalIP > 0)
      .sort((a, b) => b.totalIP - a.totalIP || b.count - a.count)
      .slice(0, max);

    const mapped = await attachProfiles(ranked);

    if (mapped.length === 0) {
      return getAllTimeCategoryLeaderboard(category, max);
    }

    return mapped;
  } catch (err) {
    errorDev("Operation failed:", err);
    return getAllTimeCategoryLeaderboard(category, max);
  }
}

export async function getLeaderboardByCategory(
  category,
  rangeDays = TIME_RANGES.ALL_TIME,
  max = LEADERBOARD_PAGE_SIZE
) {
  if (!category || category === OVERALL_LEADERBOARD_KEY) {
    return getTimeBoundOverallLeaderboard(rangeDays, max);
  }
  return getTimeBoundCategoryLeaderboard(category, rangeDays, max);
}

export async function getTopSellers(rangeDays = TIME_RANGES.ALL_TIME, max = LEADERBOARD_PAGE_SIZE) {
  return getLeaderboardByCategory(IMPACT_LEADERBOARD_CATEGORIES.TOP_GROWERS, rangeDays, max);
}

export async function getTopPriceContributors(rangeDays = TIME_RANGES.ALL_TIME, max = LEADERBOARD_PAGE_SIZE) {
  return getLeaderboardByCategory(IMPACT_LEADERBOARD_CATEGORIES.PRICE_SCOUTS, rangeDays, max);
}

export async function getTopFixers(rangeDays = TIME_RANGES.ALL_TIME, max = LEADERBOARD_PAGE_SIZE) {
  return getLeaderboardByCategory(IMPACT_LEADERBOARD_CATEGORIES.FIXERS, rangeDays, max);
}

export async function getCO2Stats() {
  try {
    const q = query(
      collection(db, "products"),
      where("status", "in", ["SOLD", "COMPLETED"])
    );
    const snap = await getDocs(q);
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    let totalCO2 = 0;
    let last30DaysCO2 = 0;
    let totalTransactions = 0;
    const byCategory = {};

    for (const d of docs) {
      const kg = normalizeToKg(d.quantity, d.unit);
      const co2 = estimateCO2Saved(kg);
      totalCO2 += co2;
      totalTransactions += 1;

      const ts = d.soldAt?.seconds
        ? d.soldAt.seconds * 1000
        : d.updatedAt?.seconds
          ? d.updatedAt.seconds * 1000
          : d.createdAt?.seconds
            ? d.createdAt.seconds * 1000
            : null;
      if (ts && ts >= thirtyDaysAgo) {
        last30DaysCO2 += co2;
      }

      const cat = d.category || "Other";
      byCategory[cat] = (byCategory[cat] || 0) + co2;
    }

    return {
      totalCO2: Math.round(totalCO2 * 100) / 100,
      totalTransactions,
      avgPerTransaction: totalTransactions > 0 ? Math.round((totalCO2 / totalTransactions) * 100) / 100 : 0,
      last30DaysCO2: Math.round(last30DaysCO2 * 100) / 100,
      byCategory,
    };
  } catch (err) {
    errorDev("Operation failed:", err);
    return { totalCO2: 0, totalTransactions: 0, avgPerTransaction: 0, last30DaysCO2: 0, byCategory: {} };
  }
}

export async function getUserImpact(userId) {
  if (!userId) {
    return {
      totalIP: 0,
      fromSelling: 0,
      fromPriceReports: 0,
      fromRatings: 0,
      fromFixers: 0,
      fromOther: 0,
      co2Saved: 0,
      recentActions: [],
    };
  }

  try {
    const userSnap = await getDoc(doc(db, "users", userId)).catch(() => null);

    const [recentEventsSnap, allEventsSnap, productCountSnap, priceCountSnap] = await Promise.all([
      getDocs(
        query(
          collection(db, "impact_points_events"),
          where("userId", "==", userId),
          orderBy("createdAt", "desc"),
          limit(30)
        )
      ).catch(async () => {
        try {
          return await getDocs(
            query(
              collection(db, "impact_points_events"),
              where("userId", "==", userId),
              limit(30)
            )
          );
        } catch (err) {
          errorDev("Operation failed:", err);
          return null;
        }
      }),
      getDocs(
        query(
          collection(db, "impact_points_events"),
          where("userId", "==", userId)
        )
      ).catch(() => null),
      getCountFromServer(query(collection(db, "products"), where("poster.userId", "==", String(userId)))).catch(() => null),
      getCountFromServer(query(collection(db, "priceSubmissions"), where("userId", "==", String(userId)))).catch(() => null),
    ]);

    const userData = userSnap?.exists?.() ? userSnap.data() : {};
    const breakdown = userData.impactBreakdown || {};
    const events = (allEventsSnap?.docs || []).map((d) => d.data() || {});
    const eventTotals = aggregateImpactEvents(events);

    const productsListedCount = safeNumber(productCountSnap?.data?.()?.count ?? productCountSnap?.count);
    const pricesSubmittedCount = safeNumber(priceCountSnap?.data?.()?.count ?? priceCountSnap?.count);

    const productListingPoints = mergeWithFallback(
      breakdown.productListingPoints,
      eventTotals.productListingPoints,
      safeNumber(userData.productsListed) * 5,
      productsListedCount * 5
    );
    const profileRatingPoints = safeNumber(userData.ratingPoints);

    const productRatingPoints = mergeWithFallback(
      breakdown.productRatingPoints,
      eventTotals.productRatingPoints
    );
    const priceValidatedPoints = mergeWithFallback(
      breakdown.priceValidatedPoints,
      eventTotals.priceValidatedPoints,
      safeNumber(userData.pricesSubmitted) * 10,
      pricesSubmittedCount * 10
    );
    const helpfulFeedbackPoints = mergeWithFallback(
      breakdown.helpfulFeedbackPoints,
      eventTotals.helpfulFeedbackPoints
    );

    const fromSelling = productListingPoints + productRatingPoints;
    const fromPriceReports = priceValidatedPoints;
    const fromRatings = mergeWithFallback(
      productRatingPoints,
      profileRatingPoints
    );
    const fromFixers = helpfulFeedbackPoints;

    const knownEventPoints =
      eventTotals.productListingPoints +
      eventTotals.productRatingPoints +
      eventTotals.priceValidatedPoints +
      eventTotals.helpfulFeedbackPoints +
      eventTotals.other;

    const totalIP = mergeWithFallback(userData.points, knownEventPoints, fromSelling + fromPriceReports + fromFixers);
    const knownBreakdown = fromSelling + fromPriceReports + fromFixers;
    const fromOther = Math.max(0, totalIP - knownBreakdown);

    const recentActions = (recentEventsSnap?.docs || []).map((d) => {
      const data = d.data() || {};
      return {
        id: d.id,
        action: data.action,
        type: mapImpactType(data.action),
        points: Number(data.points || 0),
        timestamp: data.createdAt || null,
        impactData: data.metadata || {},
      };
    });

    const co2Saved = Number(userData.co2Saved || 0);

    return {
      totalIP,
       fromSelling,
       fromPriceReports,
      fromRatings,
      fromFixers,
      fromOther,
      co2Saved: Math.round(co2Saved * 100) / 100,
      recentActions,
      profile: {
        displayName: userData.displayName || userData.username || "",
        photoURL: userData.photoURL || "",
        donatedKg: 0,
        peopleHelped: 0,
        rating: userData.rating || 0,
      },
    };
  } catch (err) {
    errorDev("Operation failed:", err);
    return {
      totalIP: 0,
      fromSelling: 0,
      fromPriceReports: 0,
      fromRatings: 0,
      fromFixers: 0,
      fromOther: 0,
      co2Saved: 0,
      recentActions: [],
    };
  }
}

function mapImpactType(action) {
  if (action === "product_posted") return IMPACT_TYPES.PRODUCT_LISTING;
  if (action === "price_report_validated") return IMPACT_TYPES.PRICE_CONTRIBUTION;
  if (action === "product_rating_received") return IMPACT_TYPES.RATING;
  return action || "other";
}

function normalizeToKg(quantity, unit) {
  const q = Number(quantity);
  if (!Number.isFinite(q) || q <= 0) return 0;
  const u = (unit || "kg").toUpperCase();
  if (u === "P") return q * 0.4;
  if (u === "L") return q * 1;
  return q;
}
